import { Link } from 'react-router-dom'
import { FileCheck, Shield, Globe, CheckCircle, ArrowLeft } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { SEOHead } from '@/components/SEOHead'

export default function CompliancePolicy() {
  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden">
      <SEOHead
        title="Compliance Policy | Sycek OSINT"
        description="Sycek OSINT compliance policy — GDPR, data handling standards, regulatory framework, and our commitment to lawful intelligence collection."
        canonical="/compliance"
      />
      {/* Background Effects */}
      <div className="absolute inset-0 bg-gradient-to-br from-cyan-900/20 via-purple-900/20 to-red-900/20"></div>
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#3b82f61a_1px,transparent_1px),linear-gradient(to_bottom,#3b82f61a_1px,transparent_1px)] bg-[size:4rem_4rem]"></div>

      {/* Header */}
      <div className="relative z-10 border-b border-cyan-500/30 bg-black/90 backdrop-blur-sm shadow-[0_4px_20px_rgba(34,211,238,0.15)]">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-3 hover:opacity-80 transition-opacity">
            <img 
              src="/logo.jpeg" 
              alt="Sycek Logo" 
              className="h-10 w-10 object-contain drop-shadow-[0_0_15px_rgba(34,197,94,0.6)]"
              onError={(e) => {
                e.currentTarget.style.display = 'none'
              }}
            />
            <div>
              <h1 className="text-xl font-bold bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 bg-clip-text text-transparent drop-shadow-[0_0_10px_rgba(34,211,238,0.6)] font-mono">
                SYCEK OSINT
              </h1>
              <p className="text-xs text-green-400 font-mono drop-shadow-[0_0_5px_rgba(34,197,94,0.6)]">[SYSTEM ACTIVE]</p>
            </div>
          </Link>
          <Link to="/">
            <Button variant="ghost" className="text-cyan-400 hover:bg-cyan-500/20 border border-cyan-500/50">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 py-12 max-w-4xl">
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-4">
            <FileCheck className="w-8 h-8 text-blue-400" />
            <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-400 via-cyan-400 to-green-400 bg-clip-text text-transparent">
              Compliance Policy
            </h1>
          </div>
          <p className="text-gray-400 text-sm font-mono">
            Our commitment to regulatory compliance and industry standards
          </p>
        </div>

        <Card className="bg-gray-900/50 border border-cyan-500/30 backdrop-blur-sm">
          <CardContent className="p-8 space-y-6">
            <section>
              <h2 className="text-2xl font-bold text-cyan-400 mb-4 flex items-center">
                <Shield className="w-6 h-6 mr-2" />
                Regulatory Compliance
              </h2>
              <div className="text-gray-300 space-y-3">
                <p>Sycek OSINT is committed to maintaining compliance with applicable laws and regulations:</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div className="bg-gray-800/50 border border-green-500/30 rounded-lg p-4">
                    <h3 className="font-bold text-green-400 mb-2">GDPR</h3>
                    <p className="text-sm">General Data Protection Regulation (EU/EEA)</p>
                    <ul className="text-xs mt-2 space-y-1">
                      <li>✓ Data subject rights</li>
                      <li>✓ Lawful processing</li>
                      <li>✓ Data breach notification</li>
                      <li>✓ Privacy by design</li>
                    </ul>
                  </div>
                  <div className="bg-gray-800/50 border border-green-500/30 rounded-lg p-4">
                    <h3 className="font-bold text-green-400 mb-2">CCPA</h3>
                    <p className="text-sm">California Consumer Privacy Act</p>
                    <ul className="text-xs mt-2 space-y-1">
                      <li>✓ Consumer privacy rights</li>
                      <li>✓ Disclosure requirements</li>
                      <li>✓ Opt-out mechanisms</li>
                      <li>✓ Non-discrimination</li>
                    </ul>
                  </div>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-cyan-400 mb-4 flex items-center">
                <CheckCircle className="w-6 h-6 mr-2" />
                Industry Certifications
              </h2>
              <div className="text-gray-300 space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-800/50 border border-blue-500/30 rounded-lg p-4">
                    <h3 className="font-bold text-blue-400 mb-2">SOC 2 Type II</h3>
                    <p className="text-sm">Annual security, availability, and confidentiality audits</p>
                  </div>
                  <div className="bg-gray-800/50 border border-blue-500/30 rounded-lg p-4">
                    <h3 className="font-bold text-blue-400 mb-2">ISO 27001</h3>
                    <p className="text-sm">Information Security Management System certification</p>
                  </div>
                  <div className="bg-gray-800/50 border border-blue-500/30 rounded-lg p-4">
                    <h3 className="font-bold text-blue-400 mb-2">NIST Framework</h3>
                    <p className="text-sm">Cybersecurity Framework alignment</p>
                  </div>
                  <div className="bg-gray-800/50 border border-blue-500/30 rounded-lg p-4">
                    <h3 className="font-bold text-blue-400 mb-2">PCI DSS</h3>
                    <p className="text-sm">Payment Card Industry Data Security Standards</p>
                  </div>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-cyan-400 mb-4 flex items-center">
                <Globe className="w-6 h-6 mr-2" />
                Data Protection & Privacy
              </h2>
              <div className="text-gray-300 space-y-3">
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong className="text-green-400">Data Minimization:</strong> Collect only necessary data for service provision</li>
                  <li><strong className="text-green-400">Purpose Limitation:</strong> Use data only for stated purposes</li>
                  <li><strong className="text-green-400">Storage Limitation:</strong> Retain data only as long as necessary</li>
                  <li><strong className="text-green-400">Right to Access:</strong> Users can request access to their data</li>
                  <li><strong className="text-green-400">Right to Erasure:</strong> Users can request data deletion</li>
                  <li><strong className="text-green-400">Data Portability:</strong> Export data in machine-readable formats</li>
                  <li><strong className="text-green-400">Privacy Impact Assessments:</strong> Regular PIAs for new features</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-cyan-400 mb-4">Legal & Ethical Compliance</h2>
              <div className="text-gray-300 space-y-3">
                <p><strong className="text-cyan-400">OSINT Ethics:</strong></p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Compliance with applicable OSINT regulations and guidelines</li>
                  <li>Respect for privacy and data protection laws</li>
                  <li>Prohibition of unauthorized surveillance or stalking</li>
                  <li>Responsible use of intelligence gathering tools</li>
                  <li>Adherence to terms of service of data sources</li>
                </ul>
                <p className="mt-4"><strong className="text-yellow-400">User Responsibility:</strong></p>
                <p className="ml-4">
                  Users are responsible for ensuring their use of the Service complies with all applicable laws 
                  in their jurisdiction, including data protection, privacy, and investigation regulations.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-cyan-400 mb-4">Audit & Monitoring</h2>
              <div className="text-gray-300 space-y-3">
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong className="text-green-400">Regular Audits:</strong> Annual third-party security and compliance audits</li>
                  <li><strong className="text-green-400">Internal Assessments:</strong> Quarterly internal compliance reviews</li>
                  <li><strong className="text-green-400">Continuous Monitoring:</strong> Real-time compliance monitoring and alerting</li>
                  <li><strong className="text-green-400">Documentation:</strong> Comprehensive compliance documentation and evidence</li>
                  <li><strong className="text-green-400">Training:</strong> Regular compliance training for all staff</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-cyan-400 mb-4">Industry Standards</h2>
              <div className="text-gray-300 space-y-3">
                <p>We adhere to industry best practices and standards:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong className="text-cyan-400">OWASP Top 10:</strong> Protection against common security vulnerabilities</li>
                  <li><strong className="text-cyan-400">CIS Controls:</strong> Center for Internet Security Critical Security Controls</li>
                  <li><strong className="text-cyan-400">CWE Top 25:</strong> Common Weakness Enumeration prevention</li>
                  <li><strong className="text-cyan-400">Secure SDLC:</strong> Security integrated into software development lifecycle</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-cyan-400 mb-4">Compliance Contacts</h2>
              <div className="text-gray-300 space-y-3">
                <div className="bg-gray-800/50 border border-cyan-500/30 rounded-lg p-4 font-mono">
                  <p><strong className="text-cyan-400">Compliance Officer:</strong> compliance@sycek.io</p>
                  <p><strong className="text-cyan-400">Data Protection Officer (DPO):</strong> dpo@sycek.io</p>
                  <p><strong className="text-cyan-400">Privacy Inquiries:</strong> privacy@sycek.io</p>
                  <p><strong className="text-cyan-400">Legal:</strong> legal@sycek.io</p>
                  <p><strong className="text-cyan-400">Address:</strong> Sycek OSINT, Bangalore KA, INDIA</p>
                </div>
              </div>
            </section>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

